import { useRef, useEffect } from "react";
import { Box, Typography, CircularProgress, useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { apiPostRequest } from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";

import CommunityCard from "./components/CommunityCard";
import IncomeGraph from "./components/IncomeGraph";
import { useCommunitiesOverview } from "./hooks/useCommunitiesOverview";

const fmt = (n) => Number(n).toLocaleString("en-US");
const CARD_BORDER = "1px solid rgba(234, 234, 234, 1)";
const TEXT_MAIN = "rgba(47, 43, 61, 0.9)";
const TEXT_SUB = "rgba(71, 84, 103, 1)";
const AMBER = "rgba(180, 120, 20, 1)";

const o = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: { xs: "75px", md: "0px" },
    paddingBottom: "24px",
    maxWidth: "100%",
    overflowX: "hidden",
  },
  aggGrid: (cols) => ({
    display: "grid",
    // Stacked rows on mobile (numbers get the full width), one row on desktop.
    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: `repeat(${cols}, minmax(0, 1fr))` },
    gap: "12px",
  }),
  aggTile: { border: CARD_BORDER, borderRadius: "12px", backgroundColor: "#fff", padding: "16px", display: "flex", alignItems: "center", gap: "12px", minWidth: 0 },
  aggIcon: (bg) => ({ width: "44px", height: "44px", minWidth: "44px", borderRadius: "50%", backgroundColor: bg, display: "flex", alignItems: "center", justifyContent: "center" }),
  aggValue: { fontSize: "22px", fontWeight: 600, lineHeight: "120%", color: TEXT_MAIN },
  aggLabel: { fontSize: "13px", color: TEXT_SUB },
  aggSub: { fontSize: "12px", color: TEXT_SUB },

  centered: { display: "flex", alignItems: "center", justifyContent: "center", minHeight: "320px" },

  graphCard: { border: CARD_BORDER, borderRadius: "12px", backgroundColor: "#fff", padding: "18px", maxWidth: "100%", overflow: "hidden" },

  // Horizontally scrollable carousel: native touch scroll + a visible thin
  // scrollbar, plus mouse click-and-drag (wired below) so desktop can slide too.
  // Snapping only on phones (a swipe lands on a card); on desktop it made drags
  // and trackpad scrolls jump card-by-card, so the track glides freely there.
  track: {
    display: "flex",
    gap: "16px",
    overflowX: "auto",
    marginTop: "8px",
    paddingBottom: "10px",
    cursor: "grab",
    userSelect: "none",
    scrollSnapType: { xs: "x proximity", md: "none" },
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(47, 43, 61, 0.25) transparent",
    "&:active": { cursor: "grabbing" },
    "& img": { WebkitUserDrag: "none" },
    "&::-webkit-scrollbar": { height: "8px" },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(47, 43, 61, 0.22)", borderRadius: "4px" },
    "&::-webkit-scrollbar-track": { backgroundColor: "rgba(47, 43, 61, 0.05)", borderRadius: "4px" },
  },
  slide: {
    // ~2.5 cards visible on desktop so the peeking card signals "scrollable".
    flex: { xs: "0 0 82%", sm: "0 0 56%", md: "0 0 38%" },
    minWidth: 0,
    scrollSnapAlign: "start",
  },
};

const CommunitiesOverviewContainer = () => {
  const { t, i18n } = useTranslation("page-communities");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const lang = (i18n.language || "en").toLowerCase().startsWith("he") ? "he" : "en";
  const isRTL = lang === "he";
  const loc = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v[lang] ?? v.en : v);

  const { data: m, isLoading, error } = useCommunitiesOverview();

  // Multi-community feature only: with one community (as its CM or MCM) there
  // is no portfolio to manage, so send the viewer to their community dashboard
  // instead of showing a "list of one". (The sidebar link is hidden for them too.)
  const communityCount = m?.communities.length ?? 0;
  useEffect(() => {
    if (!isLoading && m && communityCount <= 1) {
      navigate("/dashboard", { replace: true });
    }
  }, [isLoading, m, communityCount, navigate]);

  const cur = m?.currencySymbol || "₪";
  const money = (n) => `${cur}${fmt(n)}`;
  const list = m?.communities || [];

  // Mouse drag-to-scroll (desktop). Touch uses native scrolling.
  const trackRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0 });
  const onPointerDown = (e) => {
    if (e.pointerType === "touch") return;
    drag.current = { down: true, startX: e.clientX, startScroll: trackRef.current.scrollLeft };
  };
  const onPointerMove = (e) => {
    if (!drag.current.down) return;
    trackRef.current.scrollLeft = drag.current.startScroll - (e.clientX - drag.current.startX);
  };
  const endDrag = () => {
    drag.current.down = false;
  };

  // Smoothly bring a card to the reading-start edge (used by the "needs" tile).
  const scrollToIndex = (index) => {
    const track = trackRef.current;
    const card = track?.children?.[index];
    if (!track || !card) return;
    const tRect = track.getBoundingClientRect();
    const cRect = card.getBoundingClientRect();
    const start = track.scrollLeft;
    const target = start + (isRTL ? cRect.right - tRect.right : cRect.left - tRect.left);
    const t0 = performance.now();
    const ease = (x) => 1 - Math.pow(1 - x, 3);
    const prevSnap = track.style.scrollSnapType;
    track.style.scrollSnapType = "none";
    const tick = () => {
      const p = Math.min(1, (performance.now() - t0) / 350);
      track.scrollLeft = start + (target - start) * ease(p);
      if (p < 1) setTimeout(tick, 16);
      else track.style.scrollSnapType = prevSnap;
    };
    tick();
  };
  // Pending approvals belong to a community's direct manager, so they only
  // count (and only show on the card) where the viewer IS that manager —
  // regardless of any other role they hold (e.g. Manager-of-Managers).
  const pendingOf = (c) => (c.managedByMe ? (c.needs.joinRequests || 0) + (c.needs.supplierOffers || 0) : 0);
  const managesAny = list.some((c) => c.managedByMe);

  const jumpToFirstNeedy = () => {
    const i = list.findIndex((c) => pendingOf(c) > 0);
    if (i >= 0) scrollToIndex(i);
  };

  const agg = list.reduce(
    (acc, c) => {
      acc.members += c.members.current;
      acc.suppliers += c.suppliers;
      acc.revenue += c.revenueThisMonth;
      acc.earnings += c.earningsThisMonth;
      acc.needs += pendingOf(c);
      return acc;
    },
    { members: 0, suppliers: 0, revenue: 0, earnings: 0, needs: 0 },
  );

  // The current community lives in the server session, so switch to the clicked
  // community first (same call as the sidebar switcher), then load the target
  // page fresh so every screen reads the new community. Used by the card's open
  // arrow (its dashboard) and its needs rows (its members / suppliers).
  // `?tab=2` is the join-requests tab on both pages (same links the
  // notification CTAs use), so a needs row lands on the pending items.
  const OPEN_TARGETS = { dashboard: "/dashboard", members: "/my-members?tab=2", suppliers: "/my-suppliers?tab=2" };
  const openCommunity = async (community, target) => {
    try {
      await apiPostRequest(endpoints.switchCommunity, { communityId: community.id });
      window.location.assign(OPEN_TARGETS[target] || OPEN_TARGETS.dashboard);
    } catch (error) {
      console.error("Failed to switch community:", error);
    }
  };

  // On phones the 12-month graph is too dense — show only the last 3 months.
  const monthsToShow = isMobile ? 3 : 12;
  const graphSeries = list.map((c) => ({ name: loc(c.name), color: c.color, values: c.income12mo.slice(-monthsToShow) }));
  const graphLabels = (m?.months || []).slice(-monthsToShow);

  const aggTiles = [
    { key: "communities", icon: <GroupsOutlinedIcon sx={{ color: "#7367F0", fontSize: 22 }} />, bg: "rgba(242, 243, 251, 1)", value: fmt(list.length), label: t("agg.communities") },
    { key: "members", icon: <PeopleAltOutlinedIcon sx={{ color: "#ff9f43", fontSize: 22 }} />, bg: "rgba(255, 244, 229, 1)", value: fmt(agg.members), label: t("agg.members") },
    { key: "revenue", icon: <PaymentsOutlinedIcon sx={{ color: "rgba(36, 139, 112, 1)", fontSize: 22 }} />, bg: "rgba(219, 240, 236, 1)", value: money(agg.revenue), label: t("agg.revenue"), sub: t("agg.earnings", { money: money(agg.earnings) }) },
    // No directly managed community → nothing of theirs can need attention; a
    // standing "0" would misread as "all handled", so the tile is left out.
    managesAny
      ? { key: "needs", icon: <NotificationsActiveOutlinedIcon sx={{ color: AMBER, fontSize: 22 }} />, bg: "rgba(255, 244, 229, 1)", value: fmt(agg.needs), label: t("agg.needs", { count: agg.needs }), valueColor: agg.needs > 0 ? AMBER : TEXT_MAIN, onClick: agg.needs > 0 ? jumpToFirstNeedy : undefined }
      : null,
  ].filter(Boolean);

  if (isLoading || (!m && !error) || (m && communityCount <= 1)) {
    return (
      <Box sx={{ ...o.page, ...o.centered }}>
        <CircularProgress sx={{ color: "rgba(36, 139, 112, 1)" }} />
      </Box>
    );
  }

  if (error || !m) {
    return (
      <Box sx={{ ...o.page, ...o.centered }}>
        <Typography sx={{ color: TEXT_SUB }}>{t("load_error")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={o.page}>
      <Box sx={o.aggGrid(aggTiles.length)}>
        {aggTiles.map((tile) => (
          <Box
            key={tile.key}
            onClick={tile.onClick}
            sx={{ ...o.aggTile, ...(tile.onClick ? { cursor: "pointer", "&:hover": { backgroundColor: "rgba(245, 246, 250, 1)" } } : {}) }}
          >
            <Box sx={o.aggIcon(tile.bg)}>{tile.icon}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...o.aggValue, ...(tile.valueColor ? { color: tile.valueColor } : {}) }}>{tile.value}</Typography>
              <Typography sx={o.aggLabel}>{tile.label}</Typography>
              {tile.sub ? <Typography sx={o.aggSub}>{tile.sub}</Typography> : null}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={o.graphCard}>
        <IncomeGraph series={graphSeries} labels={graphLabels} currencySymbol={cur} />
      </Box>

      <Box
        ref={trackRef}
        sx={o.track}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        {list.map((community) => (
          <Box key={community.id} sx={o.slide}>
            <CommunityCard community={community} cover={community.cover} loc={loc} money={money} t={t} isRTL={isRTL} onOpen={openCommunity} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CommunitiesOverviewContainer;
